import React, { useEffect, useId, useState } from 'react';
import APIAgent from '../../../agents/apiAgent';
import { usePropertyBrowse } from '../hooks/usePropertyBrowse';
import { formatPrice } from '../utils';
import { isOfferable, isSold } from '../../../utils/listingStatus';
import './PropertyBrowse.css';
import { isVacantLand, VACANT_LAND_LABEL } from '../../../utils/propertyKind';

const PAGE_SIZE = 8;

function HouseCard({ house, onSelect, index = 0 }) {
  const thumb =
    (Array.isArray(house.image_urls) && house.image_urls[0]) ||
    house.image_url ||
    (Array.isArray(house.images) && house.images[0]) ||
    null;
  const beds = house.bedrooms ?? house.beds ?? null;
  const baths = house.bathrooms ?? house.baths ?? null;
  const sqft = house.square_feet ?? house.sqft ?? null;
  const price = formatPrice(house.price || house.list_price || 0);
  // Browse includes sold homes (they stay visible as comps), but the backend
  // rejects offers on them with 409 `listing_not_offerable`.
  const offerable = isOfferable(house.listing_status);
  const sold = isSold(house.listing_status);

  return (
    <article
      className="pb-card"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      <div className="pb-card__media">
        {thumb ? (
          <img src={thumb} alt={house.address || 'Property'} className="pb-card__img" />
        ) : (
          <div className="pb-card__img pb-card__img--placeholder" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" />
              <path d="M9 21V12h6v9" />
            </svg>
          </div>
        )}
        <div className="pb-card__img-overlay">
          <span className="pb-card__price-badge">{price}</span>
          <span className={`pb-card__status-pill${sold ? ' pb-card__status-pill--sold' : ''}`}>
            {sold ? 'Sold' : 'Available'}
          </span>
        </div>
      </div>

      <div className="pb-card__body">
        <h3 className="pb-card__address">{house.address}</h3>
        <p className="pb-card__location">
          {[house.city, house.state, house.zip_code].filter(Boolean).join(', ')}
        </p>
        <ul className="pb-card__facts" aria-label="Property details">
          {isVacantLand(house) ? (
            <li>{VACANT_LAND_LABEL}</li>
          ) : (
            <>
              {beds != null && <li><span className="pb-card__fact-num">{beds}</span> bd</li>}
              {baths != null && <li><span className="pb-card__fact-num">{baths}</span> ba</li>}
              {sqft != null && <li><span className="pb-card__fact-num">{Number(sqft).toLocaleString()}</span> sqft</li>}
            </>
          )}
        </ul>
      </div>

      {offerable ? (
        <button
          type="button"
          className="pb-card__cta"
          onClick={() => onSelect(house)}
        >
          Make offer
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </button>
      ) : (
        <button type="button" className="pb-card__cta" disabled>
          {sold ? 'Sold — not accepting offers' : 'Not accepting offers'}
        </button>
      )}
    </article>
  );
}

function CardSkeleton() {
  return (
    <div className="pb-card pb-card--skel" aria-hidden="true">
      <div className="pb-card__skel-media" />
      <div className="pb-card__body">
        <div className="pb-card__skel-line pb-card__skel-line--title" />
        <div className="pb-card__skel-line pb-card__skel-line--sub" />
        <div className="pb-card__skel-line pb-card__skel-line--facts" />
      </div>
      <div className="pb-card__skel-cta" />
    </div>
  );
}

export default function PropertyBrowse({ userId, onSelect, initialPropertyId }) {
  const browse = usePropertyBrowse({ userId, pageSize: PAGE_SIZE });
  const searchId = useId();
  const [didDeepLink, setDidDeepLink] = useState(false);
  const [deepLinkBlocked, setDeepLinkBlocked] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');

  useEffect(() => {
    browse.fetchFavorites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  useEffect(() => {
    if (!initialPropertyId || didDeepLink) return;
    let cancelled = false;
    (async () => {
      try {
        const house = await APIAgent.request(`/api/houses/${initialPropertyId}`);
        if (cancelled || !house) return;
        if (!isOfferable(house.listing_status)) {
          // Don't open the offer form for a home the backend will refuse.
          setDeepLinkBlocked(
            `${house.address || 'This home'} ${isSold(house.listing_status) ? 'has sold' : 'is no longer on the market'} and is not accepting offers.`
          );
          setDidDeepLink(true);
          return;
        }
        if (typeof onSelect === 'function') {
          onSelect(house);
          setDidDeepLink(true);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('PropertyBrowse: failed to deep-link to house', err);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPropertyId, didDeepLink]);

  const totalPages = Math.max(1, Math.ceil((browse.totalCount || 0) / PAGE_SIZE));
  const displayedHouses =
    activeFilter === 'favorites' ? browse.favorites : browse.houses;

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    if (filter === 'favorites') browse.setSearch('');
  };

  return (
    <div className="property-browse">
      <header className="pb-hero" aria-label="New offer overview">
        <div className="pb-hero__left">
          <p className="pb-hero__eyebrow">
            <span className="pb-hero__eyebrow-dot" aria-hidden="true" />
            New offer
          </p>
          <h2 className="pb-hero__title">Find your property</h2>
          <p className="pb-hero__subtitle">
            Browse available listings, select your property, and submit an offer in minutes.
          </p>
        </div>
        <div className="pb-hero__stats" aria-label="Browsing summary">
          <div className="pb-hero__stat">
            <strong className="pb-hero__stat-num">{browse.totalCount || 0}</strong>
            <span className="pb-hero__stat-label">Available</span>
          </div>
          <div className="pb-hero__stat-divider" aria-hidden="true" />
          <div className="pb-hero__stat">
            <strong className="pb-hero__stat-num">{browse.favorites.length}</strong>
            <span className="pb-hero__stat-label">Saved</span>
          </div>
          <div className="pb-hero__stat-divider" aria-hidden="true" />
          <div className="pb-hero__stat">
            <strong className="pb-hero__stat-num">{totalPages}</strong>
            <span className="pb-hero__stat-label">{totalPages === 1 ? 'Page' : 'Pages'}</span>
          </div>
        </div>
      </header>

      <div className="pb-controls">
        <div className="pb-controls__filters" role="group" aria-label="Property filter">
          <button
            type="button"
            className={`pb-controls__filter-btn${activeFilter === 'all' ? ' pb-controls__filter-btn--active' : ''}`}
            onClick={() => handleFilterChange('all')}
          >
            All properties
          </button>
          <button
            type="button"
            className={`pb-controls__filter-btn${activeFilter === 'favorites' ? ' pb-controls__filter-btn--active' : ''}`}
            onClick={() => handleFilterChange('favorites')}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill={activeFilter === 'favorites' ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
            Saved ({browse.favorites.length})
          </button>
        </div>

        <div className="pb-controls__search-row">
          {activeFilter === 'all' && (
            <>
              <label htmlFor={searchId} className="pb-sr-only">Search properties</label>
              <div className="pb-controls__search-wrap">
                <svg className="pb-controls__search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
                </svg>
                <input
                  id={searchId}
                  type="text"
                  className="pb-controls__search-input"
                  placeholder="City, address, or zip…"
                  value={browse.search}
                  onChange={(e) => browse.setSearch(e.target.value)}
                />
                {browse.search && (
                  <button
                    type="button"
                    className="pb-controls__search-clear"
                    onClick={() => browse.setSearch('')}
                    aria-label="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
            </>
          )}

          <button
            type="button"
            className="pb-controls__refresh-btn"
            onClick={browse.refetch}
            disabled={browse.loading}
            aria-label="Refresh listings"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
              <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
              <path d="M8 16H3v5" />
            </svg>
            {browse.loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="pb-status-row" aria-live="polite">
        {browse.loading ? (
          <span className="pb-status-row__text">Loading listings…</span>
        ) : (
          <span className="pb-status-row__text">
            {activeFilter === 'favorites'
              ? `${browse.favorites.length} saved propert${browse.favorites.length === 1 ? 'y' : 'ies'}`
              : displayedHouses.length > 0
                ? `${displayedHouses.length}${browse.totalCount ? ` of ${browse.totalCount}` : ''} propert${displayedHouses.length === 1 ? 'y' : 'ies'}`
                : 'No properties found'}
          </span>
        )}
        {browse.search && activeFilter === 'all' && (
          <span className="pb-status-row__chip">"{browse.search}"</span>
        )}
      </div>

      {deepLinkBlocked && (
        <p className="pb-error" role="alert">{deepLinkBlocked}</p>
      )}

      {browse.error && (
        <p className="pb-error" role="alert">{browse.error}</p>
      )}

      {browse.loading ? (
        <div className="pb-grid" role="status" aria-busy="true" aria-live="polite" aria-label="Loading properties">
          {[0, 1, 2, 3, 4, 5].map((i) => <CardSkeleton key={i} />)}
        </div>
      ) : displayedHouses.length === 0 ? (
        <div className="pb-empty">
          <div className="pb-empty__icon" aria-hidden="true">
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.25">
              <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
            </svg>
          </div>
          <p className="pb-empty__heading">
            {activeFilter === 'favorites' ? 'No saved properties yet' : 'No properties found'}
          </p>
          <p className="pb-empty__sub">
            {activeFilter === 'favorites'
              ? 'Browse all properties and save the ones you like.'
              : 'Try a different search term or clear your filters.'}
          </p>
          {activeFilter === 'favorites' && (
            <button type="button" className="pb-empty__cta" onClick={() => handleFilterChange('all')}>
              Browse all properties
            </button>
          )}
        </div>
      ) : (
        <div className="pb-grid">
          {displayedHouses.map((house, i) => (
            <HouseCard key={house.id} house={house} onSelect={onSelect} index={i} />
          ))}
        </div>
      )}

      {activeFilter === 'all' && browse.totalCount > PAGE_SIZE && (
        <nav className="pb-pagination" aria-label="Pagination">
          <button
            type="button"
            className="pb-pagination__btn"
            onClick={() => browse.setPage(Math.max(1, browse.page - 1))}
            disabled={browse.page <= 1}
          >
            ← Prev
          </button>
          <span className="pb-pagination__status">
            {browse.page} / {totalPages}
          </span>
          <button
            type="button"
            className="pb-pagination__btn"
            onClick={() => browse.setPage(Math.min(totalPages, browse.page + 1))}
            disabled={browse.page >= totalPages}
          >
            Next →
          </button>
        </nav>
      )}
    </div>
  );
}
